import type { GroupSelection } from './groupFilter';

/**
 * "The rules list cannot answer for this group right now."
 *
 * Every rule-derived control on the opener row reads `rulesByPairId.get(pairId)`
 * and treats a MISS as "this pair has no rule" — which is also what an
 * unanswerable list looks like. This resolver is the one place that decides
 * when a miss is not knowledge, so a control can render UNKNOWN instead of a
 * confident wrong value (see `resolveMakerSwitchState`'s `ruleUnknown`).
 *
 * Pure + separately tested because both terms are mutation-silent in the row
 * tests: each covers a DIFFERENT way the list goes unanswerable, and dropping
 * either leaves the bug alive on that path only.
 *
 * TWO terms, because there are exactly two ways to fail:
 *
 * - **`!hasData` — there is no list at all.** Covers the cold load, a fetch
 *   that FAILED and left nothing, and one more state that enumerating status
 *   flags misses entirely: `setQueryData` with an updater returning
 *   `undefined` promotes an UNRESOLVED query to `'success'` with
 *   `data: undefined` — a v3 behaviour `useEntry.ts` already guards against
 *   twice ("an updater returning `undefined` still leaves a rebuilt, data-less
 *   query behind"). In that state `isLoading`, `isError` and `isPreviousData`
 *   are ALL false with no list in hand, so a flag-enumerating predicate says
 *   "settled" and every row paints a confident OFF.
 *
 * - **`isPreviousData` — the list is real, but it is the PREVIOUS group's.**
 *   A GROUP SWITCH, and the case operators hit constantly. `keepPreviousData`
 *   deliberately keeps it on screen while the new key resolves, so the list is
 *   non-empty and every row of the new group misses its lookup.
 *
 * 🚨 **`isError` must NOT be a term of its own.** react-query's error state is
 * STICKY and RETAINS data: the error reducer keeps `data`, and the next fetch
 * only resets to `'loading'` when there is no `dataUpdatedAt` — so a failing
 * BACKGROUND refetch leaves `status:'error'` next to a perfectly good
 * current-group list, for the whole outage. Treating that as unanswerable is
 * worse than the bug it would fix: it skeletons every rule-less row (no arming
 * a new pair) AND disables the Soccer drawer's Turn on / Turn off — the DISARM
 * path blocked precisely during the incident where disarming is what you want.
 * `!hasData` already covers the error case that actually matters, because
 * under `'error'` the keepPreviousData branch is skipped, so a present `data`
 * can only be this key's own successful fetch.
 *
 * `'all'` is excluded because the query is `enabled: false` there and never
 * resolves: arriving at `'all'` FROM a group leaves `isPreviousData` stuck true
 * forever. Today `EntryPage` renders the group-picker landing instead of the
 * table under `'all'`, so no row exists — but a permanently-unknown page is a
 * bad thing to leave one refactor away.
 *
 * KNOWN RESIDUAL: "usable" is not "fresh". Under a sustained `GET /rules`
 * outage the retained list ages without bound, so a rule created OUT OF BAND
 * (another operator, another tab) reads as "no rule" → a confident OFF. Our own
 * creates are covered (`seedRuleIntoLists` + the optimistic patch). Choosing
 * the other way is strictly worse (it blocks disarm); the honest complement is
 * a page-level "rules list failed to refresh" banner, which does not exist yet.
 */
export function resolveRulesPending(opts: {
  /** Is the served list the PREVIOUS group's? (`keepPreviousData`) */
  isPreviousData?: boolean;
  /** Does the query hold a list at all? `rules !== undefined`. */
  hasData?: boolean;
  groupSelection: GroupSelection;
}): boolean {
  if (opts.groupSelection.kind === 'all') return false;
  // `!== true` / `=== true`, never truthiness: several page tests mock
  // `useEntryRules` with `{ data }` only, and this feeds a `boolean` prop a row
  // turns into a fail-safe display decision. Never hand it a non-boolean.
  return opts.isPreviousData === true || opts.hasData !== true;
}
